import {
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Icons,
  Input,
  Stack,
  Switch,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { Fragment } from 'react';
import { Control, Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getAppointmentDate, getAppointmentTimeFrom } from '../../../api/created/appointment.ts';
import {
  getGetLabOrdersByPatientQueryKey,
  useCreateSputumTest,
  useUpdateSputumTest,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AppointmentDto,
  CreateQuantiferonTestDtoInterpretation,
  CreateSputumTestDto,
  CreateSputumTestDtoInterpretation,
  CreateTuberculinTestDtoInterpretation,
  LabOrderDtoType,
  SputumTestMaterialDto,
  SputumTestMaterialDtoTestMaterial,
  SputumTestMaterialResultDto,
  SputumTestMaterialResultDtoDetectionMethod,
  SputumTestMaterialResultDtoTestResult,
} from '../../../api/generated/model';
import { queryClient } from '../../../api/queryClient.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { theme } from '../../../theme.ts';
import { getDefaultValueForDateInput, getDefaultValueForLocalTimeInput } from '../../../utils/date.ts';
import { blobToBase64 } from '../../../utils/file.ts';
import { toLabOrderSamplingContact } from '../../../utils/labOrder.ts';
import { getDetectionMethodName, getTestResultName, getTestTypeName } from '../../../utils/labReport.tsx';
import { getStringOrUndefined } from '../../../utils/string.ts';
import { DeleteLabOrderButton } from './DeleteLabOrderButton.tsx';
import { LabOrderDetailsForm } from './LabOrderDetailsForm.tsx';
import { ResultInterpretationButtonGroup } from './ResultInterpretationButtonGroup.tsx';

interface MaterialFormValues {
  testMaterial: SputumTestMaterialDtoTestMaterial;
  otherMaterial: string | null;
  materialResults: MaterialResultFormValues[];
}

interface MaterialResultFormValues {
  detectionMethod: SputumTestMaterialResultDtoDetectionMethod;
  testResult: SputumTestMaterialResultDtoTestResult | null;
}

interface FormValues {
  samplingDate: string;
  samplingTime: string;
  samplingAddress: AddressDto | null;
  samplingContact: AddressContactDto | null;
  materials: MaterialFormValues[];
  interpretation: CreateQuantiferonTestDtoInterpretation | null;
  file: File | null;
  notes: string;
  isTodo: boolean;
}
export function CreateSputumResultForm({
  appointment,
  patientId,
  onClose,
  currentResult,
  currentResultId,
}: {
  appointment?: AppointmentDto | null;
  patientId: string;
  onClose: () => void;
  currentResult?: CreateSputumTestDto;
  currentResultId?: string;
}) {
  const isEditing = !!currentResultId;
  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(appointment, currentResult),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = formMethods;

  const { fields: materialFields } = useFieldArray({ control, name: 'materials' });

  const materialValues = watch('materials');

  const isSamplingAddressDisabled = !!currentResult?.samplingAddress;
  const isSamplingContactDisabled = !!currentResult?.samplingContact;
  const isFileInputDisabled = !!currentResult?.file;
  const isInterpretationRequired =
    !!currentResult?.interpretation && currentResult.interpretation !== CreateTuberculinTestDtoInterpretation.PENDING;

  const createSputumTestMutation = useCreateSputumTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim speichern des Testresultates ist ein Fehler aufgetreten'),
    },
  });

  const updateSputumTestMutation = useUpdateSputumTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Aktualisieren des Testresultats ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = async (data: FormValues) => {
    const createSputumTestDto: CreateSputumTestDto = {
      patientId: patientId,
      samplingDate: data.samplingDate,
      samplingTime: getStringOrUndefined(data.samplingTime),
      samplingAddress: data.samplingAddress ?? undefined,
      samplingContact: toLabOrderSamplingContact(data.samplingContact),
      appointmentId: appointment?.id,
      materials: data.materials
        .map((material) => ({
          testMaterial: material.testMaterial,
          otherMaterial: getStringOrUndefined(material.otherMaterial),
          testMaterialResults: material.materialResults.filter(
            ({ testResult }) => testResult,
          ) as SputumTestMaterialResultDto[],
        }))
        .filter((material) => material.testMaterialResults.length > 0) as SputumTestMaterialDto[],
      interpretation: data.interpretation ?? CreateSputumTestDtoInterpretation.PENDING,
      file: data.file
        ? {
            fileName: data.file.name,
            file: await blobToBase64(data.file),
          }
        : undefined,
      notes: getStringOrUndefined(data.notes),
      isTodo: data.isTodo,
    };
    if (isEditing) {
      updateSputumTestMutation.mutate({ id: currentResultId, data: createSputumTestDto });
    } else {
      createSputumTestMutation.mutate({ data: createSputumTestDto });
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Stack gap={2}>
          <Typography level={'title-lg'}>Sputumtest</Typography>
          <LabOrderDetailsForm
            samplingDate={'samplingDate'}
            samplingTime={'samplingTime'}
            samplingAddress={'samplingAddress'}
            samplingContact={'samplingContact'}
            file={'file'}
            isSamplingAddressDisabled={isSamplingAddressDisabled}
            isSamplingContactDisabled={isSamplingContactDisabled}
            fileInputName={'PDF Laborbefund'}
            fileInputPlaceHolder={currentResult?.file?.fileName ?? 'Laborbefund'}
            fileInputMimeType={'application/pdf'}
            isFileInputDisabled={isFileInputDisabled}
            isEditing={isEditing}
          />
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Resultat</Typography>
          <Stack gap={1.5}>
            {materialFields.map((materialField, index) => (
              <Fragment key={materialField.id}>
                <Card variant={'outlined'} sx={{ flex: 1, bgcolor: theme.vars.palette.background.surface }}>
                  <Typography level={'title-md'}>{getTestTypeName(materialField.testMaterial)}</Typography>
                  {materialField.testMaterial !== SputumTestMaterialDtoTestMaterial.SPUTUM_FROM_THE_AIRWAYS && (
                    <Stack direction={'row'}>
                      <FormLabel sx={{ flex: 1 }}>Beschreibung</FormLabel>
                      <Input
                        sx={{ flex: 3 }}
                        {...register(`materials.${index}.otherMaterial`)}
                        placeholder={'Anderes Material spezifizieren'}
                      />
                    </Stack>
                  )}
                  <SelectMaterialResult
                    index={index}
                    control={control}
                    disabled={
                      (materialField.testMaterial ===
                        SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS ||
                        materialField.testMaterial === SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL) &&
                      !materialValues[index].otherMaterial
                    }
                  />
                </Card>
              </Fragment>
            ))}
            <FormControl error={!!errors.interpretation}>
              <FormLabel>
                <Typography>Resultat {isInterpretationRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'interpretation'}
                control={control}
                rules={{ required: isInterpretationRequired }}
                render={({ field: { onChange, value } }) => (
                  <ResultInterpretationButtonGroup
                    value={value}
                    onChange={onChange}
                    resultOptions={[
                      CreateQuantiferonTestDtoInterpretation.POSITIVE,
                      CreateQuantiferonTestDtoInterpretation.NEGATIVE,
                      CreateQuantiferonTestDtoInterpretation.INDETERMINATE,
                    ]}
                  />
                )}
              />
              {errors.interpretation && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Notiz</Typography>
          <Textarea {...register('notes')} minRows={2} maxRows={4} />
          {!isEditing && (
            <Controller
              name="isTodo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  endDecorator={'Handlung erforderlich (ist ToDo)'}
                  sx={{ paddingTop: '1rem', alignSelf: 'flex-start' }}
                />
              )}
            />
          )}
          <FormActionButtons
            onAbortClick={handleOnClose}
            disableSubmit={!isFormDirty}
            isLoading={createSputumTestMutation.isPending}>
            {currentResultId && (
              <DeleteLabOrderButton
                orderId={currentResultId}
                patientId={patientId}
                appointmentId={currentResult?.appointmentId}
                orderType={LabOrderDtoType.SPUTUM}
              />
            )}
          </FormActionButtons>
        </Stack>
      </form>
    </FormProvider>
  );
}

function SelectMaterialResult({
  index,
  control,
  disabled,
}: {
  index: number;
  control: Control<FormValues>;
  disabled: boolean;
}) {
  const { fields: resultFields, update: updateResult } = useFieldArray({
    control,
    name: `materials.${index}.materialResults`,
  });

  return (
    <>
      {resultFields.map((resultField, index) => (
        <Fragment key={resultField.id}>
          <Stack direction={'row'}>
            <FormLabel sx={{ flex: 1 }}>{getDetectionMethodName(resultField.detectionMethod)}</FormLabel>
            <ToggleButtonGroup
              disabled={disabled}
              sx={{ flex: 3, alignItems: 'center' }}
              value={resultField.testResult}>
              {Object.values(SputumTestMaterialResultDtoTestResult).map((result) => (
                <Button
                  value={result}
                  key={result}
                  onClick={() =>
                    updateResult(index, { detectionMethod: resultField.detectionMethod, testResult: result })
                  }>
                  {getTestResultName(result)}
                </Button>
              ))}
              <IconButton
                disabled={!resultField.testResult}
                onClick={() =>
                  updateResult(index, {
                    detectionMethod: resultField.detectionMethod,
                    testResult: null,
                  })
                }>
                <Icons.HighlightOff />
              </IconButton>
            </ToggleButtonGroup>
          </Stack>
        </Fragment>
      ))}
    </>
  );
}

function getDefaultValues(appointment?: AppointmentDto | null, currentResult?: CreateSputumTestDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(
      currentResult?.samplingDate,
      getAppointmentDate(appointment) ?? undefined,
    ),
    samplingTime: currentResult?.samplingTime
      ? getDefaultValueForLocalTimeInput(currentResult.samplingTime)
      : (getAppointmentTimeFrom(appointment) ?? ''),
    samplingAddress: currentResult?.samplingAddress ?? null,
    samplingContact: currentResult?.samplingContact ?? null,
    materials: [
      {
        testMaterial: SputumTestMaterialDtoTestMaterial.SPUTUM_FROM_THE_AIRWAYS,
        otherMaterial: null,
        materialResults: getMaterialResultDefaultValue(
          SputumTestMaterialDtoTestMaterial.SPUTUM_FROM_THE_AIRWAYS,
          currentResult?.materials,
        ),
      },
      {
        testMaterial: SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS,
        otherMaterial:
          currentResult?.materials.find(
            (material) => material.testMaterial === SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS,
          )?.otherMaterial ?? '',
        materialResults: getMaterialResultDefaultValue(
          SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS,
          currentResult?.materials,
        ),
      },
      {
        testMaterial: SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL,
        otherMaterial:
          currentResult?.materials.find(
            (material) => material.testMaterial === SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL,
          )?.otherMaterial ?? '',
        materialResults: getMaterialResultDefaultValue(
          SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL,
          currentResult?.materials,
        ),
      },
    ],
    interpretation:
      currentResult?.interpretation === CreateSputumTestDtoInterpretation.PENDING
        ? null
        : (currentResult?.interpretation ?? null),
    file: null,
    notes: currentResult?.notes ?? '',
    isTodo: currentResult?.isTodo ?? false,
  };
}

function getMaterialResultDefaultValue(
  testMaterial: SputumTestMaterialDtoTestMaterial,
  materials?: SputumTestMaterialDto[],
): MaterialResultFormValues[] {
  const materialResults = materials?.find((material) => material.testMaterial === testMaterial)?.testMaterialResults;

  return [
    {
      detectionMethod: SputumTestMaterialResultDtoDetectionMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION,
      testResult:
        getMaterialResultTestResult(
          SputumTestMaterialResultDtoDetectionMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION,
          materialResults,
        ) ?? null,
    },
    {
      detectionMethod: SputumTestMaterialResultDtoDetectionMethod.MICROSCOPIC_DIRECT_DETECTION,
      testResult:
        getMaterialResultTestResult(
          SputumTestMaterialResultDtoDetectionMethod.MICROSCOPIC_DIRECT_DETECTION,
          materialResults,
        ) ?? null,
    },
    {
      detectionMethod: SputumTestMaterialResultDtoDetectionMethod.CULTURE,
      testResult:
        getMaterialResultTestResult(SputumTestMaterialResultDtoDetectionMethod.CULTURE, materialResults) ?? null,
    },
  ];
}

function getMaterialResultTestResult(
  detectionMethod: SputumTestMaterialResultDtoDetectionMethod,
  materialResults?: SputumTestMaterialResultDto[],
) {
  return materialResults?.find((result) => result.detectionMethod === detectionMethod)?.testResult;
}
